import { useTextTranslation } from "../../utils/useTextTranslation";
import usePublicationMetadata from "./usePublicationMetadata";
import { isComingSoon, publicationLabel } from "../../utils/publication";
import { TRACK_IDS, toTrackId } from "../../analytics/trackIds";
import React, { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { SessionContext } from "../../context/SessionContext";
import { getCinemaCopy } from "../../statics/cinemaCopy";
import { getBannerSource } from "../../utils/responsiveBanner";
import {
  openVodDetail,
  VodBackgroundContext
} from "../newVodDetail/vodModalNavigation";
import ResponsiveBanner from "../cinema/ResponsiveBanner";
import CinemaIcon from "../cinema/CinemaIcon";
import { LoadingState } from "../status/StatusView";
import ModalPortal from "../modalPortal";
import ProgramDetail from "../programDetail";
import defaultPoster from "../../assets/images/default-vod-horizontal-poster.png";
import useShowcaseSlider from "./useShowcaseSlider";
import "./ShowcaseBanner.scss";

const imageFor = (item) => getBannerSource(item?.posters, defaultPoster);
const Image = ({ item, className, enabled = true }) => (
  <ResponsiveBanner
    className={className}
    enabled={enabled}
    src={imageFor(item)}
    fallback={defaultPoster}
  />
);

export default function Mainboard({
  mainBoardVods: items,
  onBrowse,
  presentation
}) {
  const translateText = useTextTranslation();
  const withPublication = usePublicationMetadata(items);
  const history = useHistory();
  const location = useLocation();
  const session = useContext(SessionContext) || {};
  const covered = useContext(VodBackgroundContext);
  const copy = { ...getCinemaCopy(), ...presentation };
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(
    document.visibilityState === "visible"
  );
  const [reducedMotion, setReducedMotion] = useState(false);
  const [showProgram, setShowProgram] = useState(false);
  const count = items?.length || 0;
  const { trackRef, index, select, interacting } = useShowcaseSlider(
    items,
    reducedMotion
  );
  const item = items?.[index];

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(media.matches);
    update();
    if (media.addEventListener) {
      media.addEventListener("change", update);
      return () => media.removeEventListener("change", update);
    }
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);
  useEffect(() => {
    setShowProgram(false);
  }, [items]);

  useEffect(() => {
    const update = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  useEffect(() => {
    if (
      !visible ||
      covered ||
      showProgram ||
      focused ||
      interacting ||
      reducedMotion ||
      count < 2
    )
      return;
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") select(index + 1);
    }, 10000);
    return () => clearInterval(timer);
  }, [
    visible,
    covered,
    showProgram,
    focused,
    reducedMotion,
    count,
    index,
    select,
    interacting
  ]);

  const open = (play = false) => {
    if (item.type === "CATEGORY") {
      if (onBrowse) onBrowse();
    } else if (item.type === "PROGRAM") {
      if (play) history.push(`/dashboard/livetv/${item.channelId}`);
      else setShowProgram(true);
    } else openVodDetail(history, item.id, play ? "?play=1" : "");
  };
  const slides =
    count > 1 ? [items[count - 1], ...items, items[0]] : items || [];
  const renderSlide = (entry, slot) => {
    entry = withPublication(entry);
    const position = count > 1 ? (slot - 1 + count) % count : 0;
    const clone = count > 1 && (slot === 0 || slot === count + 1);
    const selected = position === index;
    const accessible = selected && !clone;
    const Heading = accessible ? "h1" : "h2";
    const previous = position === (index - 1 + count) % count;
    const direction = slot < index + 1 ? -1 : 1;
    const near = selected || previous || position === (index + 1) % count;
    const isCategory = entry.type === "CATEGORY";
    const heroTrackId = (id) => toTrackId(id, position + 1, clone ? `clone_${slot}` : undefined);
    const playTrackId = isCategory ? TRACK_IDS.HERO_GO_CATEGORY
      : entry.type === "PROGRAM" ? TRACK_IDS.HERO_GO_CHANNEL
      : session.hasLoggedIn && entry.isUnlocked ? TRACK_IDS.HERO_WATCH_NOW : TRACK_IDS.HERO_PREVIEW;
    const kind = isCategory
      ? copy.collection
      : entry.type === "SERIES"
      ? copy.series
      : entry.type === "PROGRAM"
      ? copy.program
      : copy.movie;
    const playLabel = isCategory
      ? copy.more
      : entry.type === "PROGRAM"
      ? copy.live
      : session.hasLoggedIn && entry.isUnlocked
      ? copy.watch
      : copy.preview;
    const genres = (Array.isArray(entry.genres) ? entry.genres : [])
      .map((genre) => (typeof genre === "string" ? genre : genre?.name))
      .filter((name) => typeof name === "string" && name.trim())
      .slice(0, 2);
    const metadata = [
      kind,
      !isCategory && genres.join(" / "),
      !isCategory && entry.year > 0 && String(entry.year)
    ].filter(Boolean);
    const age = !isCategory && entry.maturityRating?.age;
    return (
      <div
        className={`showcase-slide ${selected ? "is-selected" : ""}`}
        key={`${clone ? "clone" : "slide"}-${slot}-${entry.id}`}
        aria-hidden={clone ? "true" : undefined}
      >
        <div className="showcase-center-card">
          <Image item={entry} enabled={near} />
          <div className="showcase-center-shade" />
          <div className="showcase-copy" aria-hidden={!accessible}>
            <Heading>{translateText(entry.title)}</Heading>
            <ul className="showcase-meta" aria-label={copy.contentInfo}>
              {metadata.map((value, index) => (
                <li key={`${index}-${value}`}>{translateText(value)}</li>
              ))}
              {age && (
                <li>
                  <span className="showcase-age">
                    {age === "G" ? copy.allAges : age}
                  </span>
                </li>
              )}
            </ul>
            <div className="showcase-actions">
              {isComingSoon(entry) && <p role="status">{publicationLabel(entry)}</p>}
              {entry.publicationReady && !isComingSoon(entry) && (!isCategory || onBrowse) && (
                <button
                  type="button"
                  data-track-id={heroTrackId(playTrackId)}
                  className="showcase-primary"
                  tabIndex={accessible ? 0 : -1}
                  onClick={() => open(true)}
                >
                  <CinemaIcon name={isCategory ? "arrow" : "play"} />
                  {playLabel}
                </button>
              )}
              {!isCategory && (
                <button
                  type="button"
                  data-track-id={heroTrackId(TRACK_IDS.HERO_MORE_INFO)}
                  className="showcase-secondary"
                  tabIndex={accessible ? 0 : -1}
                  onClick={() => open()}
                >
                  <CinemaIcon name="info" />
                  {copy.info}
                </button>
              )}
            </div>
          </div>
        </div>
        {!selected && count > 1 && (
          <button
            type="button"
            data-track-id={heroTrackId(direction < 0 ? "hero_previous" : "hero_next")}
            className={`showcase-slide-select ${
              direction < 0
                ? "showcase-select-previous"
                : "showcase-select-next"
            }`}
            tabIndex={-1}
            aria-hidden="true"
            onClick={() => select(index + direction)}
          >
            <CinemaIcon name={direction < 0 ? "previous" : "next"} />
          </button>
        )}
      </div>
    );
  };
  if (items && !count) return null;

  return (
    <div className="showcase-banner">
      {!items ? (
        <LoadingState size="section" variant="hero" />
      ) : (
        <section
          className="showcase-hero"
          aria-label={copy.selection}
          aria-roledescription="carousel"
          onFocus={() => setFocused(true)}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget))
              setFocused(false);
          }}
        >
          <div className="showcase-stage">
            <div className="showcase-track" ref={trackRef}>
              {slides.map(renderSlide)}
            </div>
          </div>
        </section>
      )}
      {showProgram && item && (
        <ModalPortal viewClass="vod-modal">
          <ProgramDetail
            programData={item}
            epgProgramId={item.epgProgramId}
            closeModal={() => setShowProgram(false)}
            fromVodGrid
            setSelectedCutvProgram={session.setSelectedCutvProgram}
            selectedChannel={session.selectedChannel}
            userFrameConfig={session.userFrameConfig}
            userInfo={session.userInfo}
            location={location}
            history={history}
            setPlayUrl={session.setPlayUrl}
            playUrl={session.playUrl}
            hasLoggedIn={session.hasLoggedIn}
          />
        </ModalPortal>
      )}
    </div>
  );
}
